import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckedIcon from '@mui/icons-material/CheckBoxOutlined';
import { Box, Checkbox, FormControlLabel, FormGroup, Input, InputProps, styled, Typography } from '@mui/material';
import React from 'react';
import { EColors, getColor } from 'theme';

const Grid = styled(Box, {
    shouldForwardProp: prop => prop !== 'columns',
})<{ columns: number }>(({ theme: { spacing, breakpoints }, columns }) => ({
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gridAutoRows: 'auto',
    gap: spacing(1.25),
    ...(columns > 2
        ? {
              [breakpoints.down('sm')]: {
                  gridTemplateColumns: `repeat(2, 1fr)`,
              },
          }
        : {}),
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme: { spacing } }) => ({
    padding: 0,
    marginLeft: 0,
    '& .MuiCheckbox-root': {
        padding: 0,
        marginRight: spacing(1),
    },
    '& .MuiSvgIcon-root': {
        fontSize: spacing(2),
    },
    '& > .MuiFormControlLabel-label': {
        fontSize: '0.75rem',
        color: getColor(EColors.pureBlack),
        lineHeight: '1em',
    },
}));

const StyledInputField = styled(Input, {
    shouldForwardProp: prop => prop !== 'error',
})<InputProps & { error?: boolean }>(({ error }) => ({
    flex: 1,
    boxSizing: 'border-box',
    width: '100%',
    height: '29px',
    minHeight: '29px',
    fontSize: '0.75rem',
    border: '1px solid',
    borderColor: error ? getColor(EColors.red) : getColor(EColors.inactiveAction),
    borderRadius: '4px',
    padding: '14.5px 10px',
    color: getColor(EColors.pureBlack),
    '& .MuiInput-input': {
        padding: 0,
    },
    '& .MuiInput-input:focus': {
        outline: 'none',
        WebkitBoxShadow: 'none',
        boxShadow: 'none',
    },
    '& .MuiInput-input:focus::placeholder': {
        color: getColor(EColors.pureBlack),
        opacity: 0.5,
    },
    '& .MuiInput-input::placeholder': {
        color: error ? getColor(EColors.red) : getColor(EColors.pureBlack),
    },
}));

type CheckboxGroupProps = {
    label?: string;
    columns?: number;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    required?: boolean;
    other?: boolean;
    options: { value: number | string; label: string; disabled?: boolean }[];
    values?: (number | string)[];
    onChange: (values: (number | string)[]) => void;
    onBlur?: () => void;
};

export const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
    (
        { label, error = '', values = [], required = false, options, disabled, columns = 1, other = false, onChange },
        ref
    ) => {
        const onChangeCheckbox = (value: number | string, isSelected: boolean) => {
            const newValues = isSelected ? values.filter(v => v !== value) : [...values, value];
            onChange(newValues);
        };

        return (
            <Box>
                {label && (
                    <Box display="flex" gap={0.5} marginBottom={1}>
                        <Typography>{label}</Typography>

                        <Typography color={getColor(EColors.pureBlack, 0.5)} fontWeight={500}>
                            {required ? '*' : ''}
                        </Typography>
                    </Box>
                )}
                <FormGroup ref={ref}>
                    <Grid columns={columns}>
                        {options.map(option => {
                            const isSelected = values?.includes(option.value);
                            return (
                                <StyledFormControlLabel
                                    key={option.value}
                                    disabled={disabled || option.disabled}
                                    checked={isSelected}
                                    label={option.label}
                                    onChange={() => onChangeCheckbox(option.value, isSelected)}
                                    control={
                                        <Checkbox
                                            checkedIcon={
                                                <CheckedIcon
                                                    sx={{
                                                        color: disabled
                                                            ? getColor(EColors.pureBlack, 0.3)
                                                            : getColor(EColors.primaryAction),
                                                    }}
                                                />
                                            }
                                            name={option.label}
                                        />
                                    }
                                />
                            );
                        })}

                        {other && (
                            <Box display="flex" alignItems="center">
                                <CheckBoxOutlineBlankIcon
                                    sx={{ fontSize: '18px', color: getColor(EColors.pureBlack, 0.25), marginRight: 1 }}
                                />
                                <StyledInputField
                                    error={false}
                                    placeholder="Other"
                                    disabled={disabled}
                                    disableUnderline
                                />
                            </Box>
                        )}
                    </Grid>
                </FormGroup>
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error}
                    </Typography>
                )}
            </Box>
        );
    }
);
